import { ReactNode } from 'react';
import { House, CalendarDots, ChartBar, Gear, SquaresFour } from '@phosphor-icons/react';

export type NavigationItem = {
  link: string;
  /** i18n-nyckel ELLER rå etikett (vuxen-nav använder råa nycklar som idag). */
  text: string;
  icon: ReactNode;
};

const ChildNavIcon = ({ emoji }: { emoji: string }) => (
  <span role="img" aria-hidden style={{ fontSize: '1.5rem', lineHeight: 1 }}>
    {emoji}
  </span>
);

// Vuxen-/handledar-nav: samma 4 flikar och Phosphor-ikoner som teen/young (regular weight).
const adultIconProps = { size: 24, weight: 'regular' as const };
export const adultNavigationItems: NavigationItem[] = [
  { link: '/', text: 'Daily', icon: <House {...adultIconProps} /> },
  { link: '/week', text: 'Weekly', icon: <CalendarDots {...adultIconProps} /> },
  { link: '/statistics', text: 'Statistics', icon: <ChartBar {...adultIconProps} /> },
  { link: '/settings', text: 'Settings', icon: <Gear {...adultIconProps} /> },
];

// Barn-nav: 4 flikar med emoji-ikoner för bättre läsbarhet för barn.
// Anteckningar + Statistik döljs i barnläge men routes finns kvar i AuthRouter
// (nåbara via URL i handledarläge). i18n-nycklar under child.nav.*.
export const childNavigationItems: NavigationItem[] = [
  { link: '/', text: 'child.nav.today', icon: <ChildNavIcon emoji="🏠" /> },
  { link: '/week', text: 'child.nav.week', icon: <ChildNavIcon emoji="📅" /> },
  { link: '/stars', text: 'child.nav.stars', icon: <ChildNavIcon emoji="⭐" /> },
  { link: '/settings', text: 'child.nav.settings', icon: <ChildNavIcon emoji="⚙️" /> },
];

// Ungdoms-nav (Teen): 4 flikar, neutralare ton än barn (inga emoji-ikoner).
// "Framsteg"-fliken pekar på /progress (iteration 2: teens egna TeenProgress-vy
// via PersonaView; barn faller tillbaka på StarsPage på samma route). Ikoner är
// Phosphor-setet enligt designdoket §7/§11 (House/CalendarDots/ChartBar/Gear),
// fill-variant och 24px för att matcha MUI-navens ikonstorlek. i18n-nycklar
// under teen.nav.*.
const teenIconProps = { size: 24, weight: 'fill' as const };
export const teenNavigationItems: NavigationItem[] = [
  { link: '/', text: 'teen.nav.today', icon: <House {...teenIconProps} /> },
  { link: '/week', text: 'teen.nav.week', icon: <CalendarDots {...teenIconProps} /> },
  { link: '/progress', text: 'teen.nav.progress', icon: <ChartBar {...teenIconProps} /> },
  { link: '/settings', text: 'teen.nav.settings', icon: <Gear {...teenIconProps} /> },
];

// Handledar-nav (supervisorProfile): handledarläget har EGEN navigation, skild
// från den vuxna brukarens. Det är hela poängen med delningen — förut delade
// handledaren och den vuxna brukaren exakt samma navbar (/, /week, /statistics,
// /settings), så det fanns ingen synlig skillnad mellan lägena.
//
// Länkarna pekar på routes som redan finns i AuthRouter:
//   /supervisor → SupervisorHub (handledarens ingång/översikt)
//   /week       → veckoschemat för vald brukare
//   /settings   → SettingsPage (där vägen tillbaka till brukarläget bor)
//
// Dagsfliken ('/supervisor/day') är BORTTAGEN: den visade handledarens egen dag —
// hälsning, klar-räknare och allt — och gjorde därmed ingen nytta för den som ville
// se brukarens schema. Veckovyn visar brukarens dagar och räcker.
// Ikonerna är samma Phosphor-set som vuxen-/teen-nav, regular weight som vuxen.
// i18n-nycklar under supervisor.nav.*.
export const supervisorNavigationItems: NavigationItem[] = [
  { link: '/supervisor', text: 'supervisor.nav.hub', icon: <SquaresFour {...adultIconProps} /> },
  { link: '/week', text: 'supervisor.nav.week', icon: <CalendarDots {...adultIconProps} /> },
  { link: '/settings', text: 'supervisor.nav.settings', icon: <Gear {...adultIconProps} /> },
];

// Bakåtkompatibel default-export (vuxen-listan) — andra konsumenter kan finnas.
export const NavigationItems = adultNavigationItems;
